import type { Path } from 'slate';

import { numberToLatin, numberToRoman } from '../../util/number-formatting';
import type {
  AlineTextFormats,
  ListDefinition,
  ListLevel,
  ListLevelDefinition,
  ListMarkerFormats,
  ListMarkerNumFormat,
  ListMarkerStyles,
  ParagraphElement,
} from '../internal';
import { defaultStyle, ListMarkerStyleName } from '../internal';

/**
 * @deprecated
 */
export function createListMarkerFormats(
  args: { formats?: AlineTextFormats } = {}
): ListMarkerFormats {
  const { formats = defaultStyle.styles } = args;

  return {
    0: formats,
    1: formats,
    2: formats,
    3: formats,
    4: formats,
    5: formats,
    6: formats,
    7: formats,
    8: formats,
  };
}

export const defaultListMarkerStyleList: ListMarkerStyles[] = [
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.DECIMAL,
      ListMarkerStyleName.LOWER_LATIN,
      ListMarkerStyleName.LOWER_ROMAN,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.DECIMAL,
      ListMarkerStyleName.LOWER_LATIN,
      ListMarkerStyleName.LOWER_ROMAN,
    ],
    suffix: ')',
  }),
  createListMarkerStyles({
    names: [ListMarkerStyleName.DECIMAL_SECTION],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.UPPER_LATIN,
      ListMarkerStyleName.LOWER_LATIN,
      ListMarkerStyleName.LOWER_ROMAN,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.UPPER_ROMAN,
      ListMarkerStyleName.UPPER_LATIN,
      ListMarkerStyleName.DECIMAL,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.DECIMAL_LEADING_ZERO,
      ListMarkerStyleName.LOWER_LATIN,
      ListMarkerStyleName.LOWER_ROMAN,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.BLACK_CIRCLE,
      ListMarkerStyleName.WHITE_CIRCLE,
      ListMarkerStyleName.BLACK_SQUARE,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.BLACK_DIAMOND_MINUS_WHITE_X,
      ListMarkerStyleName.THREE_D_TOP_LIGHTED_RIGHTWARDS_ARROWHEAD,
      ListMarkerStyleName.BLACK_SQUARE,
    ],
  }),
  createListMarkerStyles({
    names: [ListMarkerStyleName.LOWER_RIGHT_SHADOWED_WHITE_SQUARE],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.RIGHTWARDS_ARROW,
      ListMarkerStyleName.BLACK_DIAMOND_BLACK_CIRCLE,
      ListMarkerStyleName.BLACK_CIRCLE,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.BLACK_STAR,
      ListMarkerStyleName.WHITE_CIRCLE,
      ListMarkerStyleName.BLACK_SQUARE,
    ],
  }),
  createListMarkerStyles({
    names: [
      ListMarkerStyleName.THREE_D_TOP_LIGHTED_RIGHTWARDS_ARROWHEAD,
      ListMarkerStyleName.WHITE_CIRCLE,
      ListMarkerStyleName.BLACK_SQUARE,
    ],
  }),
];
export const defaultListMarkerStyles: ListMarkerStyles = defaultListMarkerStyleList[0];
export const listDefinitionPresets: ListDefinition[] = defaultListMarkerStyleList.map(listStyles =>
  createListDefinition({ listStyles })
);

export function createListDefinition({
  listStyles,
  markerFormats = defaultStyle.styles,
}: {
  listStyles?: ListMarkerStyles;
  markerFormats?: ListLevelDefinition['markerFormats'];
} = {}): ListDefinition {
  return {
    0: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[0] ?? defaultListMarkerStyles[0],
    },
    1: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[1] ?? defaultListMarkerStyles[1],
    },
    2: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[2] ?? defaultListMarkerStyles[2],
    },
    3: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[3] ?? defaultListMarkerStyles[3],
    },
    4: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[4] ?? defaultListMarkerStyles[4],
    },
    5: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[5] ?? defaultListMarkerStyles[5],
    },
    6: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[6] ?? defaultListMarkerStyles[6],
    },
    7: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[7] ?? defaultListMarkerStyles[7],
    },
    8: {
      markerFormats: markerFormats ? { ...markerFormats } : undefined,
      style: listStyles?.[8] ?? defaultListMarkerStyles[8],
    },
  };
}

export function createDefaultListItemIndentation(): Pick<
  ParagraphElement,
  'indentationFirstLine' | 'indentationLeft'
> {
  return {
    indentationFirstLine: -0.25,
    indentationLeft: 0.5,
  };
}

const ORDERED_LIST_MARKER_STYLES = new Set<ListMarkerStyleName>([
  ListMarkerStyleName.DECIMAL,
  ListMarkerStyleName.DECIMAL_LEADING_ZERO,
  ListMarkerStyleName.DECIMAL_SECTION,
  ListMarkerStyleName.LOWER_LATIN,
  ListMarkerStyleName.LOWER_ROMAN,
  ListMarkerStyleName.UPPER_LATIN,
  ListMarkerStyleName.UPPER_ROMAN,
]);

export function isOrderedListMarkerStyle(name: ListMarkerStyleName): boolean {
  return ORDERED_LIST_MARKER_STYLES.has(name);
}

export function getListMarkerText(
  levelDefinition: ListLevelDefinition,
  options: { listLevel: ListLevel; listPath: Path }
): string {
  const { listLevel, listPath } = options;
  const { customText, name, prefix, suffix } = levelDefinition.style;
  // Use prefix/suffix in case customText isn't set so we maintain backwards compatability until a time when we migrate and drop deprecated prefix/suffix.
  const safeCustomText = customText ?? createLevelCustomText(name, { listLevel, prefix, suffix });

  return interpolateCustomText(name, listPath, safeCustomText);
}

// === Helpers ===

/**
 * Given a customText, interpolates in the full listPath enumeration values.
 *
 * @example
 *     interpolateCustomText(
 *       ListMarkerStyleName.DECIMAL,
 *       [30, 20, 10, 0],
 *       '%4. [%3] %2. (%1)'
 *     );
 *     // "31. [21] 11. (1)"
 */
function interpolateCustomText(
  styleName: ListMarkerStyleName,
  listPath: Path,
  customText: string
): string {
  const matches = [...customText.matchAll(/%(\d+)/g)];
  // Custom text does not use % syntax (e.g., it's a bullet character).
  if (!matches.length) return customText;

  return matches
    .reduce((acc, match, index) => {
      const oneBasedIndex = match.at(1);
      if (typeof oneBasedIndex !== 'string' || typeof match.index !== 'number') return acc;

      const zeroBasedIndex = parseInt(oneBasedIndex) - 1;
      const listLevel = listPath.at(zeroBasedIndex);
      // If the listPath doesn't have this level, ignore % syntax for lower levels.  Conforms to DOCX.
      if (listLevel === undefined) return acc;

      const nextMatch = matches.at(index + 1);
      const markerAtLevel = getListMarkerForLevel(styleName, listLevel + 1);
      if (index === 0 && match.index !== 0)
        return (
          acc +
          customText.slice(0, match.index) +
          markerAtLevel +
          customText.slice(match.index + match.length, nextMatch?.index)
        );

      return acc + markerAtLevel + customText.slice(match.index + match.length, nextMatch?.index);
    }, '')
    .trim();
}

/**
 * Given a one-indexed listLevel (1, 2, etc.) and list marker style,
 * returns the character that should be replacing that %N position.
 */
function getListMarkerForLevel(styleName: ListMarkerStyleName, listLevel: number): string {
  switch (styleName) {
    case ListMarkerStyleName.UPPER_LATIN:
      return numberToLatin(listLevel, true);
    case ListMarkerStyleName.LOWER_LATIN:
      return numberToLatin(listLevel, false);
    case ListMarkerStyleName.UPPER_ROMAN:
      return numberToRoman(listLevel, true);
    case ListMarkerStyleName.LOWER_ROMAN:
      return numberToRoman(listLevel, false);
    case ListMarkerStyleName.CUSTOM:
    case ListMarkerStyleName.DECIMAL:
    case ListMarkerStyleName.BULLET:
    case ListMarkerStyleName.BLACK_CIRCLE:
    case ListMarkerStyleName.BLACK_DIAMOND_BLACK_CIRCLE:
    case ListMarkerStyleName.BLACK_DIAMOND_MINUS_WHITE_X:
    case ListMarkerStyleName.BLACK_SQUARE:
    case ListMarkerStyleName.BLACK_STAR:
    case ListMarkerStyleName.LOWER_RIGHT_SHADOWED_WHITE_SQUARE:
    case ListMarkerStyleName.RIGHTWARDS_ARROW:
    case ListMarkerStyleName.THREE_D_TOP_LIGHTED_RIGHTWARDS_ARROWHEAD:
    case ListMarkerStyleName.WHITE_CIRCLE:
    case ListMarkerStyleName.DECIMAL_SECTION:
      return String(listLevel);
    case ListMarkerStyleName.DECIMAL_LEADING_ZERO:
      return String(listLevel).padStart(2, '0');
  }
}

/**
 * Modeled after `lvlText` in Word.
 */
export function createLevelCustomText(
  name: ListMarkerStyleName,
  options: { listLevel: ListLevel; prefix?: string; suffix?: string }
): string {
  const { listLevel, prefix = '', suffix = '.' } = options;

  switch (name) {
    case ListMarkerStyleName.BULLET:
    case ListMarkerStyleName.BLACK_CIRCLE:
      return '●';
    case ListMarkerStyleName.BLACK_DIAMOND_BLACK_CIRCLE:
      return '◆';
    case ListMarkerStyleName.BLACK_DIAMOND_MINUS_WHITE_X:
      return '❖';
    case ListMarkerStyleName.BLACK_SQUARE:
      return '■';
    case ListMarkerStyleName.BLACK_STAR:
      return '★';
    case ListMarkerStyleName.LOWER_RIGHT_SHADOWED_WHITE_SQUARE:
      return '❑';
    case ListMarkerStyleName.RIGHTWARDS_ARROW:
      return '→';
    case ListMarkerStyleName.THREE_D_TOP_LIGHTED_RIGHTWARDS_ARROWHEAD:
      return '➢';
    case ListMarkerStyleName.WHITE_CIRCLE:
      return '○';
    case ListMarkerStyleName.DECIMAL_SECTION:
      return Array.from({ length: listLevel + 1 })
        .map((_, i) => `%${i + 1}.`)
        .join('');
    case ListMarkerStyleName.CUSTOM:
    case ListMarkerStyleName.DECIMAL:
    case ListMarkerStyleName.UPPER_LATIN:
    case ListMarkerStyleName.LOWER_LATIN:
    case ListMarkerStyleName.UPPER_ROMAN:
    case ListMarkerStyleName.LOWER_ROMAN:
    case ListMarkerStyleName.DECIMAL_LEADING_ZERO:
    default:
      return `${prefix}%${listLevel + 1}${suffix}`;
  }
}

function createListMarkerStyle(
  names: [ListMarkerStyleName, ...ListMarkerStyleName[]],
  options: Parameters<typeof createLevelCustomText>[1]
): ListMarkerNumFormat {
  const name = names[options.listLevel % names.length];
  const customText = createLevelCustomText(name, options);
  return { customText, name };
}

export function createListMarkerStyles(options: {
  names: [ListMarkerStyleName, ...ListMarkerStyleName[]];
  suffix?: string;
}): ListMarkerStyles {
  const { names, suffix } = options;

  return {
    0: createListMarkerStyle(names, { listLevel: 0, suffix }),
    1: createListMarkerStyle(names, { listLevel: 1, suffix }),
    2: createListMarkerStyle(names, { listLevel: 2, suffix }),
    3: createListMarkerStyle(names, { listLevel: 3, suffix }),
    4: createListMarkerStyle(names, { listLevel: 4, suffix }),
    5: createListMarkerStyle(names, { listLevel: 5, suffix }),
    6: createListMarkerStyle(names, { listLevel: 6, suffix }),
    7: createListMarkerStyle(names, { listLevel: 7, suffix }),
    8: createListMarkerStyle(names, { listLevel: 8, suffix }),
  };
}
